/**
 * DailyPreferencesService — Préférences "du jour" de l'utilisateur
 *
 * À chaque cold start de l'app, on demande à l'utilisateur :
 *   - Quel style il veut porter aujourd'hui (chic, casual, romantique...)
 *   - Pour quelle occasion (bureau, soirée, weekend...)
 *   - Son budget cible (économique, mid, premium, luxe)
 *   - Les catégories d'articles qu'il recherche (haut, bas, sac, etc.)
 *
 * Ces préférences sont sauvées en mémoire de session uniquement (pas de
 * persistance dure : on les oublie à chaque cold start volontairement).
 *
 * Une copie léger est aussi envoyée au backend (PUT /preferences) pour
 * que le user puisse retrouver ses préférences précédentes en pré-sélection.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

const KEY_SESSION = 'outfit_daily_prefs_session';
const KEY_LAST_USED = 'outfit_daily_prefs_last';
// v1.12.183 : TTL 24h calendaire pour la modale de préférences.
// Clé par user + date Paris (YYYY-MM-DD). Si la clé existe pour aujourd'hui,
// on ne réaffiche pas la modale au reload de la page → confort utilisateur,
// surtout sur web où chaque navigation peut recharger la page.
const KEY_RESOLVED_PREFIX = 'outfit_daily_prefs_resolved';

function parisTodayKey() {
  // Format YYYY-MM-DD selon Europe/Paris (gère le DST automatiquement)
  try {
    const fmt = new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const parts = fmt.formatToParts(new Date());
    const day   = parts.find(p => p.type === 'day')?.value || '01';
    const month = parts.find(p => p.type === 'month')?.value || '01';
    const year  = parts.find(p => p.type === 'year')?.value || '1970';
    return `${year}-${month}-${day}`;
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function buildResolvedKey(userId) {
  const u = userId || 'anon';
  return `${KEY_RESOLVED_PREFIX}_${u}_${parisTodayKey()}`;
}

/**
 * Vérifie si l'utilisateur a déjà résolu (skip/submit) la modale aujourd'hui.
 * Si vrai, App.js ne réaffiche pas le popup au prochain reload.
 */
export async function hasResolvedToday(userId) {
  try {
    const v = await AsyncStorage.getItem(buildResolvedKey(userId));
    return !!v;
  } catch {
    return false;
  }
}

/**
 * Marque la modale comme résolue pour aujourd'hui (Paris TZ).
 * Au passage, purge les vieilles clés des jours précédents pour ne pas
 * polluer AsyncStorage.
 */
export async function markResolvedToday(userId) {
  try {
    const todayKey = buildResolvedKey(userId);
    await AsyncStorage.setItem(todayKey, String(Date.now()));
    // Cleanup : supprimer les clés "resolved" des autres jours pour cet user
    try {
      const all = await AsyncStorage.getAllKeys();
      const stale = all.filter(k =>
        k.startsWith(`${KEY_RESOLVED_PREFIX}_${userId || 'anon'}_`) && k !== todayKey
      );
      if (stale.length > 0) await AsyncStorage.multiRemove(stale);
    } catch {}
  } catch (e) {
    console.warn('[DailyPrefs] markResolvedToday failed:', e.message);
  }
}

/**
 * Préférences vides par défaut.
 */
export const EMPTY_PREFS = {
  styles:     [],   // ['chic', 'casual', ...]
  occasion:   null, // 'bureau' | 'soirée' | ...
  budget:     null, // 'budget' | 'mid-range' | 'premium' | 'luxury' | null (= illimité)
  categories: [],   // ['Hauts', 'Sacs', 'Chaussures', ...]
};

/**
 * Récupère les préférences de la session courante.
 * Retourne null si l'user n'a pas encore répondu cette session.
 */
export async function getSessionPrefs() {
  try {
    const raw = await AsyncStorage.getItem(KEY_SESSION);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return { ...EMPTY_PREFS, ...data };
  } catch {
    return null;
  }
}

/**
 * Sauvegarde les préférences pour la session courante.
 */
export async function setSessionPrefs(prefs) {
  try {
    const merged = { ...EMPTY_PREFS, ...prefs, savedAt: Date.now() };
    await AsyncStorage.setItem(KEY_SESSION, JSON.stringify(merged));
    // On garde aussi une copie "dernière utilisation" pour pré-remplir le popup
    // au prochain cold start (juste les choix, pas comme prefs actives).
    await AsyncStorage.setItem(KEY_LAST_USED, JSON.stringify(merged));
    setCachedPrefs(merged);
    // Notifier les écrans qui veulent réordonner leur affichage
    try {
      const { DeviceEventEmitter } = require('react-native');
      DeviceEventEmitter.emit('daily_prefs_changed', merged);
    } catch {}
    return merged;
  } catch (e) {
    console.warn('[DailyPrefs] save failed:', e.message);
    return null;
  }
}

/**
 * Récupère les "dernières préférences utilisées" pour pré-remplir le popup.
 * Retourne null si jamais utilisé.
 */
export async function getLastUsedPrefs() {
  try {
    const raw = await AsyncStorage.getItem(KEY_LAST_USED);
    if (!raw) return null;
    return { ...EMPTY_PREFS, ...JSON.parse(raw) };
  } catch {
    return null;
  }
}

/**
 * Efface les préférences de la session (force un nouveau popup).
 * Appelée au cold start de l'app pour s'assurer que le popup réapparaît
 * à chaque ouverture.
 */
export async function clearSessionPrefs() {
  try {
    await AsyncStorage.removeItem(KEY_SESSION);
  } catch {}
}

/**
 * Lecture synchrone légère via state global module.
 * Utilisée par les services (catalogue, matching) qui ont besoin d'un
 * accès rapide sans await à chaque article.
 */
let _cachedPrefs = null;
export function getCachedPrefs() {
  return _cachedPrefs;
}
export function setCachedPrefs(prefs) {
  _cachedPrefs = prefs ? { ...EMPTY_PREFS, ...prefs } : null;
}

/**
 * Initialise le cache au démarrage (lit depuis AsyncStorage).
 */
export async function hydrateCache() {
  const prefs = await getSessionPrefs();
  setCachedPrefs(prefs);
  return prefs;
}

export default {
  EMPTY_PREFS,
  getSessionPrefs,
  setSessionPrefs,
  getLastUsedPrefs,
  clearSessionPrefs,
  getCachedPrefs,
  setCachedPrefs,
  hydrateCache,
  hasResolvedToday,
  markResolvedToday,
};
